<template>
  <div class="change-records-page app-page app-page--fill">
    <el-card class="main-card">
      <div class="page-toolbar">
        <h2 class="page-title">{{ pageTitle }}</h2>
        <div class="toolbar-actions">
          <el-input
            v-model="searchKey"
            placeholder="关键字搜索"
            clearable
            style="width: 200px"
            @keyup.enter="handleSearch"
            @clear="handleSearch"
          />
          <el-button @click="handleSearch">搜索</el-button>
          <el-button
            type="primary"
            circle
            size="small"
            class="app-refresh-btn"
            :icon="Refresh"
            :loading="listLoading"
            @click="handleRefresh"
            title="刷新"
          />
        </div>
      </div>

      <div class="content-layout">
        <!-- 上部：变更记录列表 -->
        <div class="list-panel">
          <DynamicTable
            :data="tableData"
            :model="tableModel"
            :loading="listLoading"
            :show-column-selector="false"
            :show-export="false"
            :show-detail-button="can('详情')"
            :pagination="paginationConfig"
            @detail="handleSelectRecord"
            @filter="handleFilter"
            @page-change="handlePageChange"
            @page-size-change="handlePageSizeChange"
          />
        </div>

        <!-- 下部：变更详情对比 -->
        <div class="detail-panel">
          <div v-if="!selectedRecordId" class="empty-detail">
            <el-empty description="请在左侧列表中点击详情查看变更对比" />
          </div>

          <div v-else-if="detailLoading" class="detail-loading">
            <el-skeleton :rows="8" animated />
          </div>

          <div v-else-if="beforeData || afterData" class="detail-content">

            <!-- 子表数据对比：修改前 -->
            <div v-if="beforeSubTable.length > 0 || afterSubTable.length > 0" class="comparison-section">
              <h4 class="section-label section-label--before">修改前数据</h4>
              <div class="sub-table-summary" v-if="beforeData?.显示摘要">{{ beforeData.显示摘要 }}</div>
              <el-table
                :data="beforeSubTable"
                border
                size="small"
                class="data-compare-table"
                :cell-class-name="getBeforeCellClassName"
              >
                <el-table-column
                  v-for="col in subTableColumns"
                  :key="'b-' + col.prop"
                  :prop="col.prop"
                  :label="col.label"
                  :width="col.width"
                  show-overflow-tooltip
                />
              </el-table>
            </div>

            <!-- 子表数据对比：修改后 -->
            <div v-if="beforeSubTable.length > 0 || afterSubTable.length > 0" class="comparison-section">
              <h4 class="section-label section-label--after">修改后数据</h4>
              <div class="sub-table-summary" v-if="afterData?.显示摘要">{{ afterData.显示摘要 }}</div>
              <el-table
                :data="afterSubTable"
                border
                size="small"
                class="data-compare-table"
                :cell-class-name="getAfterCellClassName"
              >
                <el-table-column
                  v-for="col in subTableColumns"
                  :key="'a-' + col.prop"
                  :prop="col.prop"
                  :label="col.label"
                  :width="col.width"
                  show-overflow-tooltip
                />
              </el-table>
            </div>

            <el-empty v-if="beforeSubTable.length === 0 && afterSubTable.length === 0" description="未检测到数据变更" />
          </div>

          <div v-else class="empty-detail">
            <el-empty description="变更详情加载失败" />
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { Refresh } from '@element-plus/icons-vue'
import { list } from '@/network/index'
import DynamicTable from '@/components/DynamicTable.vue'
import { useRouteTitle } from '@/utils/useRouteTitle'
import { useButtonPermission } from '@/utils/buttonPermission'

const SUB_TABLE_KEY = '检测原始数据详细数据子表集合'
const EXCLUDED_BASIC_FIELDS = new Set([
  SUB_TABLE_KEY, 'ISDELETED', 'Id', 'id', '显示摘要',
  '修改前数据json', '修改后数据json', '修改前数据', '修改后数据',
  '备注图像列表json', '备注图像列表'
])
const EXCLUDED_SUB_COLUMNS = new Set(['ISDELETED', '检测原始数据详细数据主表id', 'Id', 'id'])
const pageTitle = useRouteTitle('原始数据变更记录')
const { can } = useButtonPermission('/RawDataManagement/ChangeRecords/index.vue')

const listLoading = ref(false)
const detailLoading = ref(false)
const tableData = ref<any[]>([])
const tableModel = ref<Record<string, string> | undefined>(undefined)
const searchKey = ref('')
const activeSearchKey = ref('')
const currentPage = ref(1)
const pageSize = ref(50)
const total = ref(0)
const filterParams = ref<any[]>([])
const selectedRecordId = ref<string>('')
const detailMeta = ref<Record<string, any>>({})
const beforeData = ref<Record<string, any> | null>(null)
const afterData = ref<Record<string, any> | null>(null)

const paginationConfig = reactive({
  show: true,
  get page() { return currentPage.value },
  get pageSize() { return pageSize.value },
  get total() { return total.value },
  pageSizes: [10, 20, 50, 100],
  layout: 'total, sizes, prev, pager, next, jumper',
  small: true
})

const safeParse = (value: unknown): Record<string, any> | null => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as Record<string, any>
  if (typeof value === 'string') {
    try { return JSON.parse(value) } catch { return null }
  }
  return null
}

const stringify = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

// ---- 基本字段对比 ----
const basicFieldDiffs = computed(() => {
  const before = beforeData.value
  const after = afterData.value
  if (!before && !after) return []

  const allKeys = new Set<string>()
  if (before) Object.keys(before).forEach(k => allKeys.add(k))
  if (after) Object.keys(after).forEach(k => allKeys.add(k))

  const rows: Array<{ field: string; beforeValue: string; afterValue: string; changed: boolean }> = []
  for (const key of allKeys) {
    if (EXCLUDED_BASIC_FIELDS.has(key)) continue
    if (key.toLowerCase().includes('json')) continue
    const bv = before ? before[key] : undefined
    const av = after ? after[key] : undefined
    if (typeof bv === 'object' && bv !== null && !Array.isArray(bv)) continue
    if (typeof av === 'object' && av !== null && !Array.isArray(av)) continue
    if (Array.isArray(bv) || Array.isArray(av)) continue

    const bs = stringify(bv)
    const as_ = stringify(av)
    rows.push({ field: key, beforeValue: bs || '-', afterValue: as_ || '-', changed: bs !== as_ })
  }
  return rows
})

// ---- 子表数据 ----
const beforeSubTable = computed<Record<string, any>[]>(() => {
  const arr = beforeData.value?.[SUB_TABLE_KEY]
  return Array.isArray(arr) ? arr : []
})

const afterSubTable = computed<Record<string, any>[]>(() => {
  const arr = afterData.value?.[SUB_TABLE_KEY]
  return Array.isArray(arr) ? arr : []
})

const subTableColumns = computed(() => {
  const colSet = new Set<string>()
  const addKeys = (rows: Record<string, any>[]) => {
    rows.forEach(row => Object.keys(row).forEach(k => colSet.add(k)))
  }
  addKeys(beforeSubTable.value)
  addKeys(afterSubTable.value)

  for (const ex of EXCLUDED_SUB_COLUMNS) colSet.delete(ex)

  const hasNonEmptyValue = (col: string): boolean => {
    const check = (rows: Record<string, any>[]) => rows.some(r => {
      const v = r[col]
      return v !== undefined && v !== null && v !== '' && v !== false
    })
    return check(beforeSubTable.value) || check(afterSubTable.value)
  }

  const ordered: Array<{ prop: string; label: string; width?: number }> = []
  if (colSet.has('Header')) {
    ordered.push({ prop: 'Header', label: '行号', width: 80 })
    colSet.delete('Header')
  }
  const numbered = Array.from(colSet)
    .filter(k => /^_\d+$/.test(k))
    .sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)))

  for (const col of numbered) {
    if (hasNonEmptyValue(col)) {
      ordered.push({ prop: col, label: col.replace('_', '列'), width: 100 })
    }
    colSet.delete(col)
  }

  for (const col of Array.from(colSet).sort()) {
    if (hasNonEmptyValue(col)) {
      ordered.push({ prop: col, label: col })
    }
  }

  return ordered
})

// ---- 子表差异映射 ----
const subTableDiffMap = computed(() => {
  const map = new Map<string, boolean>()
  const maxRows = Math.max(beforeSubTable.value.length, afterSubTable.value.length)
  for (let ri = 0; ri < maxRows; ri++) {
    const bRow = beforeSubTable.value[ri]
    const aRow = afterSubTable.value[ri]
    for (const col of subTableColumns.value) {
      const bv = bRow ? stringify(bRow[col.prop]) : ''
      const av = aRow ? stringify(aRow[col.prop]) : ''
      if (bv !== av) {
        map.set(`${ri}-${col.prop}`, true)
      }
    }
  }
  return map
})

const getBeforeCellClassName = ({ rowIndex, column }: { row: any; column: any; rowIndex: number; columnIndex: number }) => {
  return subTableDiffMap.value.has(`${rowIndex}-${column.property}`) ? 'diff-cell-bg-before' : ''
}

const getAfterCellClassName = ({ rowIndex, column }: { row: any; column: any; rowIndex: number; columnIndex: number }) => {
  return subTableDiffMap.value.has(`${rowIndex}-${column.property}`) ? 'diff-cell-bg-after' : ''
}

// ---- API 调用 ----
const loadList = async () => {
  try {
    listLoading.value = true
    const res = await list.rawDataChangeList(
      { page: currentPage.value, key: activeSearchKey.value, intPageSize: pageSize.value },
      filterParams.value
    )
    if (res && res.status === 200 && res.success && res.response) {
      tableData.value = Array.isArray(res.response.data) ? res.response.data : []
      total.value = res.response.dataCount || tableData.value.length
      if (res.response.model && typeof res.response.model === 'object') {
        tableModel.value = res.response.model
      }
    } else {
      ElMessage.error(res?.msg || '加载变更记录列表失败')
    }
  } catch (error) {
    console.error('加载变更记录列表失败:', error)
    ElMessage.error('加载变更记录列表失败')
  } finally {
    listLoading.value = false
  }
}

const loadDetail = async (id: string) => {
  try {
    detailLoading.value = true
    beforeData.value = null
    afterData.value = null
    detailMeta.value = {}

    const res = await list.rawDataChangeDetail({ id })
    if (res && res.status === 200 && res.success && res.response) {
      const resp = res.response
      detailMeta.value = {
        修改时间: resp.修改时间,
        提交人员: resp.提交人员,
        经营项目名称: resp.经营项目名称,
        检测项目名称: resp.检测项目名称
      }
      beforeData.value = safeParse(resp.修改前数据) ?? safeParse(resp.修改前数据json)
      afterData.value = safeParse(resp.修改后数据) ?? safeParse(resp.修改后数据json)
    } else {
      ElMessage.error(res?.msg || '加载变更详情失败')
    }
  } catch (error) {
    console.error('加载变更详情失败:', error)
    ElMessage.error('加载变更详情失败')
  } finally {
    detailLoading.value = false
  }
}

// ---- 事件处理 ----
const handleSearch = () => {
  activeSearchKey.value = searchKey.value
  currentPage.value = 1
  loadList()
}

const handleRefresh = () => loadList()

const handleFilter = (filters: any[]) => {
  filterParams.value = filters
  currentPage.value = 1
  loadList()
}

const handlePageChange = (page: number) => {
  currentPage.value = page
  loadList()
}

const handlePageSizeChange = (size: number) => {
  pageSize.value = size
  currentPage.value = 1
  loadList()
}

const handleSelectRecord = (row: any) => {
  const id = String(row.Id || row.id || '')
  if (!id) return
  selectedRecordId.value = id
  loadDetail(id)
}

onMounted(() => {
  loadList()
})

onUnmounted(() => {
  // cleanup
})
</script>

<style scoped lang="less">
.change-records-page {
  flex: 1;
  min-height: 0;

  .main-card {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;

    :deep(.el-card__body) {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
      gap: 12px;
      overflow: hidden;
    }
  }
}

.page-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;

  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: #303133;
    margin: 0;
  }

  .toolbar-actions {
    display: flex;
    gap: 8px;
    align-items: center;
  }
}

.content-layout {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  overflow: hidden;
}

.list-panel {
  height: 30vh;
  min-height: 18rem;
  max-height: 50vh;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  :deep(.dynamic-table-card) {
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
    
    .el-card__body {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    
    .el-table {
      flex: 1;
      min-height: 0;
      
      .el-table__body-wrapper {
        max-height: none !important;
      }
    }
    
    .pagination-wrapper {
      flex-shrink: 0;
      margin-top: 0.5rem;
    }
  }
}

.detail-panel {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 0.5rem;
  background: #fafbfc;
  padding: 1rem;
}

.empty-detail,
.detail-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

.detail-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.comparison-section {
  margin-bottom: 20px;

  h4 {
    font-size: 15px;
    font-weight: 600;
    color: #303133;
    margin: 0 0 10px 0;
  }
}

.section-label--before {
  color: #e6a23c;
  border-left: 4px solid #e6a23c;
  padding-left: 8px;
}

.section-label--after {
  color: #67c23a;
  border-left: 4px solid #67c23a;
  padding-left: 8px;
}

.sub-table-summary {
  font-size: 13px;
  color: #606266;
  margin-bottom: 8px;
  line-height: 1.5;
}

.diff-cell-before {
  background-color: #fef0f0;
  color: #f56c6c;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: 600;
}

.diff-cell-after {
  background-color: #f0f9eb;
  color: #67c23a;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: 600;
}

:deep(.diff-cell-bg-before) {
  background-color: #fef0f0 !important;
}

:deep(.diff-cell-bg-after) {
  background-color: #f0f9eb !important;
}

.diff-table,
.data-compare-table {
  width: 100%;
}

@media (max-width: 900px) {
  .list-panel {
    height: 35vh;
    min-height: 15rem;
    max-height: 45vh;
  }
}
</style>
